import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, onAuthStateChanged, sendPasswordResetEmail, signInWithEmailAndPassword, signOut, updatePassword } from '@angular/fire/auth';
import { Firestore, doc, getDoc, updateDoc } from '@angular/fire/firestore';
import { AppUser } from '../models/user.model';
import { SURVEY_ACTING_AS_STORAGE_KEY } from '../constants/storage-keys';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private router = inject(Router);

  readonly currentUser = signal<AppUser | null>(null);
  readonly isLoading = signal(true);

  constructor() {
    onAuthStateChanged(this.auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const profile = await this.fetchProfile(firebaseUser.uid);
          this.currentUser.set(profile);
        } else {
          this.currentUser.set(null);
        }
      } catch {
        this.currentUser.set(null);
      } finally {
        this.isLoading.set(false);
      }
    });
  }

  async login(email: string, password: string): Promise<AppUser> {
    const credential = await signInWithEmailAndPassword(this.auth, email, password);
    const profile = await this.fetchProfile(credential.user.uid);
    if (!profile) throw new Error('Profilo utente non trovato.');
    if (profile.disabled) throw new Error("Account disabilitato. Contattare l'amministratore.");
    this.currentUser.set(profile);
    return profile;
  }

  async logout(): Promise<void> {
    await signOut(this.auth);
    // US 7.8: the "acting as" selection of a power user does not survive the logout.
    try { sessionStorage.removeItem(SURVEY_ACTING_AS_STORAGE_KEY); } catch { /* storage unavailable */ }
    this.currentUser.set(null);
    this.router.navigate(['/login']);
  }

  async resetPassword(email: string): Promise<void> {
    await sendPasswordResetEmail(this.auth, email);
  }

  async changePassword(newPassword: string): Promise<void> {
    const firebaseUser = this.auth.currentUser;
    if (!firebaseUser) throw new Error('Utente non autenticato.');
    await updatePassword(firebaseUser, newPassword);
    await updateDoc(doc(this.firestore, 'Users', firebaseUser.uid), { passwordChanged: true });
    this.currentUser.update((u) => (u ? { ...u, passwordChanged: true } : null));
  }

  private async fetchProfile(uid: string): Promise<AppUser | null> {
    const snap = await getDoc(doc(this.firestore, 'Users', uid));
    if (!snap.exists()) return null;
    return { uid, ...snap.data() } as AppUser;
  }
}
